/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// src/components/UserDashboard.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';                 // ← picks up your axiosConfig.js
import { formatDistanceToNow } from 'date-fns';
import 'react-toastify/dist/ReactToastify.css';
import useLeadNotifications from '../hooks/useLeadNotifications';
import useAvailableJobNotifications from '../hooks/useAvailableJobNotifications';
import { io } from 'socket.io-client';
import { ToastContainer, toast } from 'react-toastify';
import { Modal, Button, Form, Card, Badge, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const formatWhen = iso => {
  if (!iso) return '';
  const dt = new Date(iso);
  return new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Europe/London'
  }).format(dt);
};

const formatDate = iso => {
  if (!iso) return '';
  const dt = new Date(iso);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];
  const dayName = dayNames[dt.getDay()];
  const day = dt.getDate();
  const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Europe/London' });
  return `${dayName} ${day} ${month}`;
};

const formatTimestamp = iso => {
  if (!iso) return 'Not available';
  const dt = new Date(iso);
  const hours = dt.getHours().toString().padStart(2, '0');
  const minutes = dt.getMinutes().toString().padStart(2, '0');
  const day = dt.getDate().toString().padStart(2, '0');
  const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Europe/London' });
  const year = dt.getFullYear().toString().slice(-2);
  return `${hours}:${minutes}-${day}/${month}/${year}`;
};

export default function UserDashboard() {
  // start listening for lead events
  useLeadNotifications();
  
  // start listening for available job events
  useAvailableJobNotifications();
  
  // Get user role for conditional rendering
  const userRole = localStorage.getItem('role');

  // state
  const [withinAreaLeads, setWithinAreaLeads] = useState([]);
  const [outsideAreaLeads, setOutsideAreaLeads] = useState([]);
  const [monitoredAreas, setMonitoredAreas] = useState([]);
  const [declinedWithin, setDeclinedWithin] = useState([]);
  const [declinedOutside, setDeclinedOutside] = useState([]);
  const [showDeclined, setShowDeclined] = useState(false);
  const [searchTerm, setSearchTerm]   = useState('');
  const [status, setStatus]           = useState('');
  const [error, setError]             = useState('');
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusNote, setStatusNote]   = useState(''); // used for accept note
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  // Message modal state
  const [messageReason, setMessageReason] = useState('Available In 30Min');
  const [messageNote, setMessageNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);

  // Listen for available job refresh events
  useEffect(() => {
    const handleRefreshAvailableJobs = async (event) => {
      console.log('🔄 Refreshing available jobs due to new job:', event.detail);
      
      try {
        setLoading(true);
        // Reload available jobs
        const { data } = await axios.get('/job-monitor/available-jobs');
        setWithinAreaLeads(data.withinArea || []);
        setOutsideAreaLeads(data.outsideArea || []);
        setMonitoredAreas(data.monitoredAreas || []);
        console.log('✅ Available jobs refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh available jobs:', error);
        setError('Failed to refresh available jobs');
      } finally {
        setLoading(false);
      }
    };

    // Add event listener
    window.addEventListener('refreshAvailableJobs', handleRefreshAvailableJobs);
    
    // Cleanup
    return () => {
      window.removeEventListener('refreshAvailableJobs', handleRefreshAvailableJobs);
    };
  }, []);

  // fetch once on mount
  useEffect(() => {
    let isMounted = true;
    
    (async () => {
      try {
        setError(''); // Clear any previous errors
        setLoading(true);
        
        // Check profile status first
        const statusRes = await axios.get('/auth/profile-status');
        setProfileStatus(statusRes.data);
        
        // Use the new filtered jobs endpoint
        const { data } = await axios.get('/job-monitor/available-jobs');
        
        if (!isMounted) return; // Prevent setting state if component unmounted
        
        console.log('Filtered jobs received:', data);
        setWithinAreaLeads(data.withinArea || []);
        setOutsideAreaLeads(data.outsideArea || []);
        setMonitoredAreas(data.monitoredAreas || []);
        // Prefetch declined jobs in background
        try {
          const dj = await axios.get('/job-monitor/declined-jobs');
          setDeclinedWithin(dj.data.withinArea || []);
          setDeclinedOutside(dj.data.outsideArea || []);
        } catch (e) {
          console.warn('Failed to load declined jobs');
        }
      } catch (err) {
        console.error('Failed to load leads:', err);
        if (isMounted) {
          if (err.response?.status === 401) {
            setError('Session expired. Please log in again.');
            // Redirect to login after a short delay
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
          } else {
            setError('Failed to load leads. Please try refreshing the page.');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen for diary refresh events from new booking requests
  useEffect(() => {
    const handleRefreshDiaryData = async (event) => {
      console.log('🔄 Refreshing diary data due to new booking request:', event.detail);
      
      try {
        setLoading(true);
        
        // Refresh the available jobs data
        const { data } = await axios.get('/job-monitor/available-jobs');
        
        console.log('Refreshed jobs received:', data);
        setWithinAreaLeads(data.withinArea || []);
        setOutsideAreaLeads(data.outsideArea || []);
        setMonitoredAreas(data.monitoredAreas || []);
        
        // Also refresh declined jobs
        try {
          const dj = await axios.get('/job-monitor/declined-jobs');
          setDeclinedWithin(dj.data.withinArea || []);
          setDeclinedOutside(dj.data.outsideArea || []);
        } catch (e) {
          console.warn('Failed to refresh declined jobs');
        }
        
        console.log('✅ Diary data refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh diary data:', error);
        toast.error('Failed to refresh diary data');
      } finally {
        setLoading(false);
      }
    };

    // Add event listener
    window.addEventListener('refreshDiaryData', handleRefreshDiaryData);
    
    // Cleanup
    return () => {
      window.removeEventListener('refreshDiaryData', handleRefreshDiaryData);
    };
  }, []);

  // Accept / Decline actions
  const handleAction = async (id, action, statusNote = '', options = {}) => {
    try {
      await axios.post(`/view-leads/${action}/${id}`, { statusNote, ...options });
      
      // Remove from both lists
      setWithinAreaLeads(prev => prev.filter(l => l._id !== id));
      setOutsideAreaLeads(prev => prev.filter(l => l._id !== id));
      
      if (action === 'accept') {
      setStatus('Job accepted and added to My Diary!');
      } else if (action === 'decline') {
        setStatus('Job declined. Customer will be notified.');
      }
      setDuplicateInfo(null);
      setShowDuplicateModal(false);
      return true;
    } catch (err) {
      console.error('Action error:', err);

      if (err.response?.status === 409 && err.response.data?.error === 'duplicate_job_detected') {
        const info = err.response.data;
        setDuplicateInfo(info);
        setShowDuplicateModal(true);
        setError('');
        setStatus('');
        if (action === 'accept') {
          setShowAcceptModal(false);
        }
        return false;
      }
      
      // Handle specific licensing/profile errors
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        if (errorData.error === 'Profile completion required') {
          setError('Please complete your profile before accepting jobs. Redirecting to profile page...');
          setTimeout(() => window.location.href = '/dashboard/update-account', 3000);
        } else if (errorData.error === 'Licensing submission required') {
          setError('Please submit your licensing information before accepting jobs. Redirecting to profile page...');
          setTimeout(() => window.location.href = '/dashboard/update-account#licensing', 3000);
        } else if (errorData.error === 'Licensing approval required') {
          setError('Your licensing information is under review. You will be notified once approved.');
        } else {
          setError(errorData.details || errorData.error || 'Action failed');
        }
      } else {
        setError('Action failed. Please try again.');
      }
      setStatus(`Failed to ${action} lead.`);
      return false;
    }
  };

  // Search filter
  const handleSearch = e => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
  };

  // Filter leads based on search term
  const filterLeads = (leads) => {
    if (!searchTerm) return leads;
    
    return leads.filter(lead => 
      lead.customerName?.toLowerCase().includes(searchTerm) ||
      lead.from?.toLowerCase().includes(searchTerm) ||
      lead.to?.toLowerCase().includes(searchTerm) ||
      lead.customerPhone?.toLowerCase().includes(searchTerm)
    );
  };

  // Modal handlers
  const openAcceptModal = (lead) => {
    setSelectedLead(lead);
    setShowAcceptModal(true);
  };

  const openDeclineModal = (lead) => {
    setSelectedLead(lead);
    setShowDeclineModal(true);
  };

  const openMessageModal = (lead) => {
    setSelectedLead(lead);
    setShowMessageModal(true);
  };

  const openAcceptModalWithNote = (lead, note) => {
    setSelectedLead(lead);
    setStatusNote(note);
    setShowAcceptModal(true);
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(false);
    setSelectedLead(null);
    setStatusNote('');
  };

  const closeDeclineModal = () => {
    setShowDeclineModal(false);
    setSelectedLead(null);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedLead(null);
    setMessageReason('Available In 30Min');
    setMessageNote('');
  };

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateInfo(null);
  };

  const handleAcceptDuplicate = async () => {
    if (!selectedLead) return;
    await handleAction(selectedLead._id, 'accept', statusNote, { forceDuplicate: true });
    closeAcceptModal();
    setDuplicateInfo(null);
    setShowDuplicateModal(false);
  };

  const handleAccept = async () => {
    if (selectedLead) {
      const success = await handleAction(selectedLead._id, 'accept', statusNote);
      if (success) {
        closeAcceptModal();
      }
    }
  };

  const handleDecline = async () => {
    if (selectedLead) {
      await handleAction(selectedLead._id, 'decline', 'Declined');
      closeDeclineModal();
    }
  };

  const handleSendMessage = async () => {
    if (!selectedLead) return;
    const fullStatusNote = `${messageReason}${messageNote ? ': ' + messageNote : ''}`;
    try {
      await axios.post(`/view-leads/respond/${selectedLead._id}`, { statusNote: fullStatusNote });
      setStatus('Message sent. Job remains available.');
    } catch (err) {
      console.error('Respond error:', err);
      setError('Failed to send message');
    } finally {
      closeMessageModal();
    }
  };

  const showJobTimestamps = (lead) => {
    const timestamps = [
      `📅 Job Date/Time: ${formatTimestamp(lead.dateTime)}`,
      `📝 Created: ${formatTimestamp(lead.dateSubmitted)}`,
      lead.updatedAt ? `✏️ Last Modified: ${formatTimestamp(lead.updatedAt)}` : null,
      lead.acceptedAt ? `✅ Accepted: ${formatTimestamp(lead.acceptedAt)}` : null,
      lead.reassignedAt ? `🔄 Reassigned: ${formatTimestamp(lead.reassignedAt)}` : null,
      lead.completedAt ? `🏁 Completed: ${formatTimestamp(lead.completedAt)}` : null
    ].filter(Boolean);

    const timestampText = timestamps.join('\n');
    toast.info(timestampText, {
      position: "top-center",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: { whiteSpace: 'pre-line' }
    });
  };

  // Render a single lead card
  const renderLeadCard = (lead) => {
    // Calculate if job has timed out
    const now = new Date();
    const leadDate = new Date(lead.dateSubmitted);
    const timeDiffMs = now - leadDate;
    const isTimedOut = timeDiffMs > 60000; // 60 seconds timeout

    return (
      <div
        key={lead._id}
        className={`card mb-3 ${lead.isASAP ? 'border-danger' : ''} ${isTimedOut ? 'border-warning' : ''}`}
        style={lead.isASAP ? { boxShadow: '0 0 0 0.1rem rgba(220,53,69,.25)' } : 
               isTimedOut ? { boxShadow: '0 0 0 0.1rem rgba(255,193,7,.25)' } : undefined}
      >
        <div className="card-body">
          {/* When - Full Width at Top */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="card-title mb-0">
                <strong>When:</strong> {formatWhen(lead.dateTime)}, {formatDate(lead.dateTime)}
              </h6>
            </div>
            {/* Status badges on separate line */}
            <div className="d-flex gap-1 mb-2">
              {lead.isASAP && (
                <span className="badge bg-danger">ASAP</span>
              )}
              {isTimedOut && (
                <span className="badge bg-warning text-dark">TIMED OUT</span>
              )}
            </div>
                         <small className="text-muted">
               Posted {formatDistanceToNow(new Date(lead.dateSubmitted))} ago
             </small>
          </div>

          {/* From and To */}
          <div className="mb-3">
            <p className="card-text mb-1">
              <strong>From:</strong> {lead.from}
            </p>
            <p className="card-text mb-1">
              <strong>To:</strong> {lead.to}
            </p>
          </div>

          {/* Customer Name and Special Requirements */}
          <div className="mb-3">
            <p className="card-text mb-1">
              <strong>Customer:</strong> {lead.customerName}
            </p>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <p className="card-text mb-0">
                <strong>Phone:</strong> {lead.customerPhone}
              </p>
              <Button 
                variant="outline-info" 
                size="sm" 
                onClick={() => showJobTimestamps(lead)}
                title="View Job Timestamps"
                className="rounded-circle"
                style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', width: '1.5rem', height: '1.5rem', backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
              >
                i
              </Button>
            </div>
            {/* Special Requirements Flags */}
            <div className="d-flex gap-2 mb-1">
              {lead.dog && (
                <Badge bg="success" className="small">🐕 Dog</Badge>
              )}
              {lead.disabled && (
                <Badge bg="info" className="small">♿ Disabled Access</Badge>
              )}
            </div>
            {lead.statusNote && (
              <p className="card-text mb-1">
                <strong>Note:</strong> {lead.statusNote}
              </p>
            )}
            {/* Timed Out Alert */}
            {isTimedOut && (
              <Alert variant="warning" className="mt-2 mb-0 py-2">
                <strong>⚠️ This job has timed out!</strong> Call the customer directly to follow up.
              </Alert>
            )}
          </div>

          {/* Buttons at Bottom */}
          <div className="d-flex justify-content-end gap-2">
            {userRole !== 'listing_only' && (
              <>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => openAcceptModal(lead)}
                >
                  Accept Job
                </Button>
                
                {/* Call button for timed out jobs - Positioned next to Accept Job */}
                {isTimedOut && lead.customerPhone && (
                  <a 
                    href={`tel:${lead.customerPhone}`}
                    className="btn btn-warning"
                    style={{ 
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    📞 Call Customer
                  </a>
                )}
                
                
                {/* Show Message button only if not timed out */}
                {!isTimedOut && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => openMessageModal(lead)}
                  >
                    Message
                  </Button>
                )}
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => openDeclineModal(lead)}
                >
                  Delete
                </Button>
              </>
            )}
            {userRole === 'listing_only' && (
              <div className="text-muted small">
                <Badge bg="info">View Only</Badge>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const filteredWithinArea = filterLeads(withinAreaLeads);
  const filteredOutsideArea = filterLeads(outsideAreaLeads);

  return (
    <div>
      {/* Sticky Header */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ 
        zIndex: 1020,
        top: '56px' // Account for navbar height
      }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h2 className="mb-0">
              Available Jobs
              {userRole === 'listing_only' && <Badge bg="info" className="ms-2">View Only</Badge>}
            </h2>
          </div>
          <div>
            <Link to="/dashboard/job-monitor" className="btn btn-outline-primary me-2">
              ⚙️
            </Link>
          </div>
        </div>

        <div className="mb-3 position-relative" style={{ maxWidth: 500 }}>
          <Form.Control
            type="text"
            placeholder="Search jobs by customer name, address, or phone..."
            value={searchTerm}
            onChange={handleSearch}
            size="sm"
            style={{ 
              fontSize: '0.875rem',
              paddingRight: '2rem' 
            }}
          />
          {searchTerm && (
            <Button
              variant="link"
              className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
              tabIndex={0}
              style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
            >
              ×
            </Button>
          )}
        </div>
      </div>

      <div className="container mt-4">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {status && (
          <Alert variant="success" onClose={() => setStatus('')} dismissible>
            {status}
          </Alert>
        )}

        {/* Profile/Licensing Status Banner */}
        {profileStatus && !profileStatus.canAcceptJobs && (
          <Alert variant="warning" className="mb-3">
            <h6>Account Setup Required</h6>
            <p className="mb-2">
              You need to complete your account setup before accepting jobs:
            </p>
            <ul className="mb-3">
              {!profileStatus.profileCompleted && (
                <li>Complete your profile information</li>
              )}
              {!profileStatus.licensingSubmitted && (
                <li>Submit your licensing information</li>
              )}
              {!profileStatus.licensingApproved && profileStatus.licensingSubmitted && (
                <li>Wait for licensing approval (under review)</li>
              )}
            </ul>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => window.location.href = '/dashboard/update-account'}
            >
              Complete Setup
            </Button>
          </Alert>
        )}

        {monitoredAreas.length > 0 && (
          <Alert variant="info" className="mb-3">
            <strong>Monitoring Areas:</strong> {monitoredAreas.join(', ')}
          </Alert>
        )}

        <div className="d-flex justify-content-end mb-3 gap-2">
          <Button variant={showDeclined ? 'secondary' : 'outline-secondary'} size="sm" onClick={() => setShowDeclined(s => !s)}>
            {showDeclined ? 'Hide' : 'Show'} Past Declined Jobs
          </Button>
        </div>

      {/* Jobs in My Area Section */}
      <Card className="mb-4">
        <Card.Header className="bg-success text-white">
          <h5 className="mb-0">
            Jobs in My Area 
            <Badge bg="light" text="dark" className="ms-2">
              {filteredWithinArea.length}
            </Badge>
          </h5>
        </Card.Header>
        <Card.Body>
          {filteredWithinArea.length === 0 ? (
            <p className="text-muted">No jobs available in your monitored areas.</p>
          ) : (
            filteredWithinArea.map(renderLeadCard)
          )}
        </Card.Body>
      </Card>

      {showDeclined && (
        <>
          <Card className="mb-4">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">Previously Declined in My Area <Badge bg="light" text="dark" className="ms-2">{declinedWithin.length}</Badge></h5>
            </Card.Header>
            <Card.Body>
              {declinedWithin.length === 0 ? (
                <p className="text-muted">No declined jobs in your area.</p>
              ) : (
                                 declinedWithin.map(lead => {
                   // Calculate if job has timed out
                   const now = new Date();
                   const leadDate = new Date(lead.dateSubmitted);
                   const timeDiffMs = now - leadDate;
                   const isTimedOut = timeDiffMs > 60000; // 60 seconds timeout

                   return (
                     <div key={lead._id} className={`card mb-2 ${lead.isASAP ? 'border-danger' : ''} ${isTimedOut ? 'border-warning' : ''}`}
                          style={lead.isASAP ? { boxShadow: '0 0 0 0.1rem rgba(220,53,69,.25)' } : 
                                 isTimedOut ? { boxShadow: '0 0 0 0.1rem rgba(255,193,7,.25)' } : undefined}>
                       <div className="card-body">
                         {/* When - Full Width at Top */}
                         <div className="mb-3">
                           <div className="d-flex justify-content-between align-items-center">
                             <h6 className="card-title mb-0">
                               <strong>When:</strong> {formatWhen(lead.dateTime)} {lead.isASAP && <span className="text-danger fw-bold ms-1">(ASAP)</span>}
                             </h6>
                             <div className="d-flex gap-1">
                               {lead.isASAP && (
                                 <span className="badge bg-danger">ASAP</span>
                               )}
                               {isTimedOut && (
                                 <span className="badge bg-warning text-dark">TIMED OUT</span>
                               )}
                             </div>
                           </div>
                           <small className="text-muted">
                             Posted {formatDistanceToNow(new Date(lead.dateSubmitted))} ago
                           </small>
                         </div>

                         {/* From and To */}
                         <div className="mb-3">
                           <p className="card-text mb-1">
                             <strong>From:</strong> {lead.from}
                           </p>
                           <p className="card-text mb-1">
                             <strong>To:</strong> {lead.to}
                           </p>
                         </div>

                         {/* Customer Name and Special Requirements */}
                         <div className="mb-3">
                           <p className="card-text mb-1">
                             <strong>Customer:</strong> {lead.customerName}
                           </p>
                           <p className="card-text mb-1">
                             <strong>Phone:</strong> {lead.phone}
                           </p>
                           {/* Special Requirements Flags */}
                           <div className="d-flex gap-2 mb-1">
                             {lead.dog && (
                               <Badge bg="success" className="small">🐕 Dog</Badge>
                             )}
                             {lead.disabled && (
                               <Badge bg="info" className="small">♿ Disabled Access</Badge>
                             )}
                           </div>
                           <div className="text-danger small mt-1">Call the customer to confirm live job after accepting</div>
                         </div>

                         {/* Buttons at Bottom */}
                         <div className="d-flex justify-content-end gap-2">
                           {userRole !== 'listing_only' ? (
                             <>
                               {/* Call button for timed out jobs */}
                               {isTimedOut && lead.phone ? (
                                 <a 
                                   href={`tel:${lead.phone}`}
                                   className="btn btn-warning btn-sm"
                                   style={{ textDecoration: 'none' }}
                                 >
                                   Call Customer
                                 </a>
                               ) : null}
                               <Button size="sm" variant="success" onClick={() => openAcceptModal(lead)}>Accept Job</Button>
                             </>
                           ) : (
                             <Badge bg="info">View Only</Badge>
                           )}
                         </div>
                       </div>
                     </div>
                   );
                 })
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">Previously Declined Outside My Area <Badge bg="light" text="dark" className="ms-2">{declinedOutside.length}</Badge></h5>
            </Card.Header>
            <Card.Body>
              {declinedOutside.length === 0 ? (
                <p className="text-muted">No declined jobs outside your area.</p>
              ) : (
                                 declinedOutside.map(lead => {
                   // Calculate if job has timed out
                   const now = new Date();
                   const leadDate = new Date(lead.dateSubmitted);
                   const timeDiffMs = now - leadDate;
                   const isTimedOut = timeDiffMs > 60000; // 60 seconds timeout

                   return (
                     <div key={lead._id} className={`card mb-2 ${lead.isASAP ? 'border-danger' : ''} ${isTimedOut ? 'border-warning' : ''}`}
                          style={lead.isASAP ? { boxShadow: '0 0 0 0.1rem rgba(220,53,69,.25)' } : 
                                 isTimedOut ? { boxShadow: '0 0 0 0.1rem rgba(255,193,7,.25)' } : undefined}>
                       <div className="card-body">
                         {/* When - Full Width at Top */}
                         <div className="mb-3">
                           <div className="d-flex justify-content-between align-items-center">
                             <h6 className="card-title mb-0">
                               <strong>When:</strong> {formatWhen(lead.dateTime)} {lead.isASAP && <span className="text-danger fw-bold ms-1">(ASAP)</span>}
                             </h6>
                             <div className="d-flex gap-1">
                               {lead.isASAP && (
                                 <span className="badge bg-danger">ASAP</span>
                               )}
                               {isTimedOut && (
                                 <span className="badge bg-warning text-dark">TIMED OUT</span>
                               )}
                             </div>
                           </div>
                           <small className="text-muted">
                             Posted {formatDistanceToNow(new Date(lead.dateSubmitted))} ago
                           </small>
                         </div>

                         {/* From and To */}
                         <div className="mb-3">
                           <p className="card-text mb-1">
                             <strong>From:</strong> {lead.from}
                           </p>
                           <p className="card-text mb-1">
                             <strong>To:</strong> {lead.to}
                           </p>
                         </div>

                         {/* Customer Name and Special Requirements */}
                         <div className="mb-3">
                           <p className="card-text mb-1">
                             <strong>Customer:</strong> {lead.customerName}
                           </p>
                           <p className="card-text mb-1">
                             <strong>Phone:</strong> {lead.phone}
                           </p>
                           {/* Special Requirements Flags */}
                           <div className="d-flex gap-2 mb-1">
                             {lead.dog && (
                               <Badge bg="success" className="small">🐕 Dog</Badge>
                             )}
                             {lead.disabled && (
                               <Badge bg="info" className="small">♿ Disabled Access</Badge>
                             )}
                           </div>
                           <div className="text-danger small mt-1">Call the customer to confirm live job after accepting</div>
                         </div>

                         {/* Buttons at Bottom */}
                         <div className="d-flex justify-content-end gap-2">
                           {userRole !== 'listing_only' ? (
                             <>
                               {/* Call button for timed out jobs */}
                               {isTimedOut && lead.phone ? (
                                 <a 
                                   href={`tel:${lead.phone}`}
                                   className="btn btn-warning btn-sm"
                                   style={{ textDecoration: 'none' }}
                                 >
                                   Call Customer
                                 </a>
                               ) : null}
                               <Button size="sm" variant="success" onClick={() => openAcceptModal(lead)}>Accept Job</Button>
                             </>
                           ) : (
                             <Badge bg="info">View Only</Badge>
                           )}
                         </div>
                       </div>
                     </div>
                   );
                 })
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Outside My Area Section */}
      <Card>
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">
            Outside My Area
            <Badge bg="light" text="dark" className="ms-2">
              {filteredOutsideArea.length}
            </Badge>
          </h5>
        </Card.Header>
        <Card.Body>
          {filteredOutsideArea.length === 0 ? (
            <p className="text-muted">No jobs available outside your monitored areas.</p>
          ) : (
            filteredOutsideArea.map(renderLeadCard)
          )}
        </Card.Body>
      </Card>

      {/* Accept Modal */}
      <Modal show={showAcceptModal} onHide={closeAcceptModal}>
        <Modal.Header closeButton>
          <Modal.Title>Accept Job</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to accept this job?</p>
          <Form.Group>
            <Form.Label>Status Note (optional):</Form.Label>
            <Form.Control
              as="textarea"
              rows="3"
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Add any notes about this job..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAcceptModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleAccept}>
            Accept Job
          </Button>
        </Modal.Footer>
      </Modal>

    {/* Duplicate Warning Modal */}
    <Modal show={showDuplicateModal} onHide={closeDuplicateModal}>
      <Modal.Header closeButton>
        <Modal.Title>Possible Duplicate Booking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          <strong>Heads up:</strong> {duplicateInfo?.message || 'This customer already has a booking around this time.'}
          Please call the customer to confirm they still require another vehicle before accepting.
        </Alert>

        {duplicateInfo?.acceptedMatches?.length > 0 && (
          <div className="mb-3">
            <h6>Already Accepted</h6>
            <ul className="mb-0 ps-3">
              {duplicateInfo.acceptedMatches.map(match => (
                <li key={match.id} className="mb-2">
                  <div><strong>{formatWhen(match.dateTime)}</strong></div>
                  {match.acceptedBy ? (
                    <>
                      <div>{match.acceptedBy.name}{match.acceptedBy.companyName ? ` — ${match.acceptedBy.companyName}` : ''}</div>
                      {match.acceptedBy.phoneNumber && (
                        <div className="small text-muted">
                          📞 {match.acceptedBy.phoneNumber}{' '}
                          <a
                            href={`tel:${match.acceptedBy.phoneNumber}`}
                            className="ms-2"
                          >
                            Call
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="small text-muted">Driver details not available.</div>
                  )}
                  {match.isSelfAssignment && (
                    <div className="small text-success">You already accepted this booking.</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {duplicateInfo?.pendingMatches?.length > 0 && (
          <div className="mb-3">
            <h6>Similar Pending Requests</h6>
            <ul className="mb-0 ps-3">
              {duplicateInfo.pendingMatches.map(match => (
                <li key={match.id}>
                  {formatWhen(match.dateTime)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {duplicateInfo?.suggestedDrivers?.length > 0 && (
          <div className="mb-0">
            <h6>Drivers Recently Involved</h6>
            <ul className="mb-0 ps-3">
              {duplicateInfo.suggestedDrivers.map(driver => (
                <li key={driver.id} className="mb-1">
                  {driver.name}{driver.companyName ? ` — ${driver.companyName}` : ''}
                  {driver.phoneNumber && (
                    <span className="small text-muted">
                      {' '}
                      (📞 {driver.phoneNumber}{' '}
                      <a href={`tel:${driver.phoneNumber}`} className="ms-1">
                        Call
                      </a>)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(!duplicateInfo?.acceptedMatches?.length && !duplicateInfo?.suggestedDrivers?.length) && (
          <Alert variant="info" className="mb-0">
            No assigned driver details are available yet. Consider contacting other drivers in your area before accepting a duplicate request.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeDuplicateModal}>
          Close
        </Button>
        {selectedLead && (
          <Button variant="success" onClick={handleAcceptDuplicate}>
            Accept Anyway &amp; Add to My Diary
          </Button>
        )}
      </Modal.Footer>
    </Modal>

      {/* Decline Modal */}
      <Modal show={showMessageModal} onHide={closeMessageModal}>
        <Modal.Header closeButton>
          <Modal.Title>Send Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select a message to send to the customer. The job will remain available.</p>
          <Form.Group className="mb-3">
            <div className="d-grid gap-2">
              {[
                'Available In 30Min',
                'Available in 1hr',
                'Available in 1.5hrs',
                'Available in 2hr',
                'Fully Booked',
                'Not Available Today',
                'No Response'
              ].map(opt => (
                <Button
                  key={opt}
                  variant={messageReason === opt ? 'primary' : 'outline-primary'}
                  size="lg"
                  onClick={() => setMessageReason(opt)}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Additional Notes (optional):</Form.Label>
            <Form.Control
              as="textarea"
              rows="3"
              value={messageNote}
              onChange={(e) => setMessageNote(e.target.value)}
              placeholder="Add any additional notes..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMessageModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendMessage}>
            Send Message
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeclineModal} onHide={closeDeclineModal}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Job From My List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Send a quick "Not Available" to the customer and remove this job from your Available Jobs.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeclineModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDecline}>
            Send Not Available & Remove
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
      </div>
    </div>
  );
}
